import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const CountUp = ({ target, suffix = "" }: { target: number; suffix?: string }) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          const duration = 1500;
          const start = Date.now();
          const tick = () => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
          };
          tick();
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="font-data text-5xl md:text-7xl font-bold text-foreground tracking-wider">
      {value}{suffix}
    </span>
  );
};

const stats = [
  { value: 42, suffix: "%", label: "of web traffic is non-human", color: "text-destructive" },
  { value: 35, suffix: "B", label: "lost to click fraud annually", color: "text-amber" },
  { value: 4, suffix: "/5", label: "SMBs have never audited traffic", color: "text-primary" },
];

const Problem = () => {
  return (
    <section id="problem" className="relative py-24 md:py-32 px-6 overflow-hidden">
      {/* Red atmospheric glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-destructive/[0.03] blur-[150px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto">
        {/* Left-aligned editorial headline */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 max-w-3xl"
        >
          <p className="text-[10px] font-mono text-destructive uppercase tracking-[0.3em] mb-6">
            The Problem
          </p>
          <h2 className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl text-foreground leading-[1.05] tracking-tight">
            42% of your traffic
            <br />
            <span className="text-destructive">isn't human.</span>
          </h2>
          <p className="text-base text-muted-foreground font-mono mt-6 max-w-lg leading-relaxed">
            You're making business decisions based on corrupted data. Your analytics are lying. Your ad budget is bleeding. And you don't even know it.
          </p>
        </motion.div>

        {/* Horizontal stats — not cards, just big numbers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
            >
              <div className={stat.color}>
                <CountUp target={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm text-muted-foreground font-mono mt-3 leading-relaxed">
                {stat.label}
              </div>
              <div className={`w-12 h-px mt-4 ${stat.color === "text-destructive" ? "bg-destructive/30" : stat.color === "text-amber" ? "bg-amber/30" : "bg-primary/30"}`} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Problem;
